import { apiClient, ApiResponse } from './api.service';
import {
  InventoryAudit,
  InventoryAuditItem,
  InventoryAuditFilters,
  InventoryAuditStats
} from '@/types/inventory-audit.types';

interface CreateAuditRequest {
  locationId: string;
  auditDate: string;
  notes?: string;
}

interface UpdateAuditRequest {
  status?: 'in_progress' | 'completed' | 'reviewed';
  notes?: string;
  totalItems?: number;
  itemsAudited?: number;
  discrepancies?: number;
  totalValue?: number;
}

interface AddAuditItemsRequest {
  items: Array<{
    ingredientId: string;
    expectedQuantity: number;
    actualQuantity: number;
    unitCost: number;
  }>;
}

interface UpdateAuditItemRequest {
  actualQuantity?: number;
  confirmedQuantity?: number;
  discrepancyReason?: string;
  isReviewed?: boolean;
}

interface AuditsListResponse {
  data: InventoryAudit[];
  meta: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

interface RecountResponse {
  auditId: string;
  itemsToRecount: number;
  itemsRecount: Array<{
    id: string;
    ingredientName: string;
    discrepancy: number;
  }>;
}

class InventoryAuditService {
  private readonly baseEndpoint = '/inventory-audits';

  async getAudits(filters: Partial<InventoryAuditFilters> = {}): Promise<ApiResponse<AuditsListResponse>> {
    const params: Record<string, string | number | boolean> = {};

    if (filters.locationId) params.locationId = filters.locationId;
    if (filters.status && filters.status !== 'all') params.status = filters.status;
    if (filters.limit) params.limit = filters.limit;
    if (filters.offset) params.offset = filters.offset;

    return apiClient.get<AuditsListResponse>(this.baseEndpoint, params);
  }

  async getAuditById(auditId: string): Promise<ApiResponse<InventoryAudit>> {
    return apiClient.get<InventoryAudit>(`${this.baseEndpoint}/${auditId}`);
  }

  async getAuditItems(auditId: string): Promise<ApiResponse<InventoryAuditItem[]>> {
    return apiClient.get<InventoryAuditItem[]>(`${this.baseEndpoint}/${auditId}/items`);
  }

  async createAudit(auditData: CreateAuditRequest): Promise<ApiResponse<InventoryAudit>> {
    return apiClient.post<InventoryAudit>(this.baseEndpoint, auditData);
  }

  async updateAudit(auditId: string, updateData: UpdateAuditRequest): Promise<ApiResponse<InventoryAudit>> {
    return apiClient.put<InventoryAudit>(`${this.baseEndpoint}/${auditId}`, updateData);
  }

  async addItemsToAudit(auditId: string, itemsData: AddAuditItemsRequest): Promise<ApiResponse<InventoryAuditItem[]>> {
    return apiClient.post<InventoryAuditItem[]>(`${this.baseEndpoint}/${auditId}/items`, itemsData);
  }

  async updateAuditItem(
    auditId: string,
    itemId: string,
    updateData: UpdateAuditItemRequest
  ): Promise<ApiResponse<InventoryAuditItem>> {
    return apiClient.put<InventoryAuditItem>(`${this.baseEndpoint}/${auditId}/items/${itemId}`, updateData);
  }

  async sendToRecount(auditId: string): Promise<ApiResponse<RecountResponse>> {
    return apiClient.post<RecountResponse>(`${this.baseEndpoint}/${auditId}/recount`);
  }

  async createRecountAudit(recountData: {
    originalAuditId: string;
    locationId: string;
    auditDate: string;
    notes: string;
    isRecount: boolean;
    items: Array<{
      ingredientId: string;
      expectedQuantity: number;
    }>;
  }): Promise<ApiResponse<{ newAuditId: string; itemsToRecount: number }>> {
    return apiClient.post<{ newAuditId: string; itemsToRecount: number }>(`${this.baseEndpoint}/create-recount`, recountData);
  }

  // Utility methods for common operations
  async completeAudit(auditId: string, totalValue: number, discrepancies: number): Promise<ApiResponse<InventoryAudit>> {
    return this.updateAudit(auditId, {
      status: 'completed',
      totalValue,
      discrepancies
    });
  }

  // Optimized method: Complete audit and update all items in single transaction
  async completeAuditWithItems(
    auditId: string,
    items: Array<{ itemId: string; data: UpdateAuditItemRequest }>,
    auditData: { totalValue: number; discrepancies: number; totalItems: number; itemsAudited: number }
  ): Promise<ApiResponse<{ audit: InventoryAudit; items: InventoryAuditItem[] }>> {
    return apiClient.post<{ audit: InventoryAudit; items: InventoryAuditItem[] }>(
      `${this.baseEndpoint}/${auditId}/complete`,
      {
        items,
        auditData: {
          ...auditData,
          status: 'completed'
        }
      }
    );
  }

  async reviewAudit(auditId: string, notes?: string): Promise<ApiResponse<InventoryAudit>> {
    return this.updateAudit(auditId, {
      status: 'reviewed',
      notes
    });
  }

  async getAuditStats(locationId?: string): Promise<ApiResponse<InventoryAuditStats>> {
    const params: Record<string, string> = {};
    if (locationId) params.locationId = locationId;

    return apiClient.get<InventoryAuditStats>(`${this.baseEndpoint}/stats`, params);
  }

  // Batch operations - Single API call for multiple updates
  async bulkUpdateItems(
    auditId: string,
    updates: Array<{ itemId: string; data: UpdateAuditItemRequest }>
  ): Promise<ApiResponse<InventoryAuditItem[]>> {
    // Use dedicated bulk update endpoint for better performance
    return apiClient.put<InventoryAuditItem[]>(`${this.baseEndpoint}/${auditId}/items/bulk`, {
      updates
    });
  }

  // Legacy method - kept for compatibility but not recommended for large datasets
  async bulkUpdateItemsLegacy(
    auditId: string,
    updates: Array<{ itemId: string; data: UpdateAuditItemRequest }>
  ): Promise<ApiResponse<InventoryAuditItem[]>> {
    const promises = updates.map(({ itemId, data }) =>
      this.updateAuditItem(auditId, itemId, data)
    );

    try {
      const results = await Promise.all(promises);
      const items = results.map(result => result.data).filter(Boolean) as InventoryAuditItem[];

      return {
        success: true,
        data: items,
        message: `${items.length} items actualizados exitosamente`
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Error al actualizar items'
      };
    }
  }

  // Validation helpers
  isValidAuditStatus(status: string): status is 'in_progress' | 'completed' | 'reviewed' {
    return ['in_progress', 'completed', 'reviewed'].includes(status);
  }

  canSendToRecount(audit: InventoryAudit): boolean {
    return audit.status === 'reviewed' && !audit.hasBeenRecounted;
  }

  canEditAudit(audit: InventoryAudit): boolean {
    return audit.status === 'in_progress';
  }

  getDiscrepancyPercentage(expected: number, actual: number): number {
    if (expected === 0) return actual === 0 ? 0 : 100;
    return Math.abs((actual - expected) / expected) * 100;
  }
}

export const inventoryAuditService = new InventoryAuditService();
export { InventoryAuditService };
export type {
  CreateAuditRequest,
  UpdateAuditRequest,
  AddAuditItemsRequest,
  UpdateAuditItemRequest,
  RecountResponse,
  AuditsListResponse
};